import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';

export interface JobTimeEntry {
  id: string;
  business_id: string;
  booking_id: string;
  resource_id: string;
  started_at: string;
  stopped_at: string | null;
  duration_seconds: number | null;
  start_method: string;
  stop_method: string | null;
  start_lat: number | null;
  start_lng: number | null;
  stop_lat: number | null;
  stop_lng: number | null;
  start_activity: string | null;
  stop_activity: string | null;
  notes: string | null;
  created_at: string;
}

interface UseJobTimerOptions {
  bookingId: string | undefined;
  resourceId: string | undefined;
  businessId: string | undefined;
}

export function useJobTimer({ bookingId, resourceId, businessId }: UseJobTimerOptions) {
  const [entries, setEntries] = useState<JobTimeEntry[]>([]);
  const [activeEntry, setActiveEntry] = useState<JobTimeEntry | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [loading, setLoading] = useState(false);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Fetch all entries for this booking + resource ──────────────────
  const fetchEntries = useCallback(async () => {
    if (!bookingId || !resourceId) return;

    const { data, error } = await supabase
      .from('job_time_entries')
      .select('*')
      .eq('booking_id', bookingId)
      .eq('resource_id', resourceId)
      .order('started_at', { ascending: false });

    if (error) {
      console.error('useJobTimer: fetch error', error.message);
      return;
    }

    const rows = (data || []) as JobTimeEntry[];
    setEntries(rows);

    const running = rows.find((e) => e.stopped_at === null) ?? null;
    setActiveEntry(running);
  }, [bookingId, resourceId]);

  // ── Load on mount + subscribe to real-time changes ─────────────────
  useEffect(() => {
    fetchEntries();

    if (!bookingId) return;

    const channel = supabase
      .channel(`job-timer-${bookingId}-${resourceId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'job_time_entries',
          filter: `booking_id=eq.${bookingId}`,
        },
        () => {
          fetchEntries();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [bookingId, resourceId, fetchEntries]);

  // ── Live elapsed-second ticker while a timer is running ────────────
  useEffect(() => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }

    if (activeEntry) {
      const calcElapsed = () => {
        const start = new Date(activeEntry.started_at).getTime();
        setElapsedSeconds(Math.floor((Date.now() - start) / 1000));
      };
      calcElapsed();
      tickRef.current = setInterval(calcElapsed, 1000);
    } else {
      setElapsedSeconds(0);
    }

    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [activeEntry]);

  // ── Total completed seconds for this booking + resource ────────────
  const totalCompletedSeconds = entries
    .filter((e) => e.stopped_at !== null && e.duration_seconds != null)
    .reduce((sum, e) => sum + (e.duration_seconds ?? 0), 0);

  // ── Start timer ────────────────────────────────────────────────────
  const startTimer = useCallback(
    async (method: string = 'manual') => {
      if (!bookingId || !resourceId || !businessId) return;
      setLoading(true);
      try {
        const { error } = await supabase.rpc('start_job_timer', {
          p_booking_id: bookingId,
          p_resource_id: resourceId,
          p_business_id: businessId,
          p_method: method,
        });
        if (error) throw error;
        await fetchEntries();
      } catch (err: any) {
        console.error('useJobTimer: start error', err.message);
      } finally {
        setLoading(false);
      }
    },
    [bookingId, resourceId, businessId, fetchEntries]
  );

  // ── Stop timer ─────────────────────────────────────────────────────
  const stopTimer = useCallback(
    async (method: string = 'manual') => {
      if (!bookingId || !resourceId) return;
      setLoading(true);
      try {
        const { error } = await supabase.rpc('stop_job_timer', {
          p_booking_id: bookingId,
          p_resource_id: resourceId,
          p_method: method,
        });
        if (error) throw error;
        await fetchEntries();
      } catch (err: any) {
        console.error('useJobTimer: stop error', err.message);
      } finally {
        setLoading(false);
      }
    },
    [bookingId, resourceId, fetchEntries]
  );

  return {
    /** All time entries for this booking + resource (newest first) */
    entries,
    /** The currently running entry, or null */
    activeEntry,
    /** Live seconds since the active entry started */
    elapsedSeconds,
    /** Sum of all completed entry durations */
    totalCompletedSeconds,
    /** True while an RPC is in-flight */
    loading,
    /** Start a new time entry */
    startTimer,
    /** Stop the running time entry */
    stopTimer,
    /** Re-fetch entries */
    refresh: fetchEntries,
  };
}
